import { useMemo } from 'react';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import {
  GetLocationPaymentMethodsRequest,
  GetPersonPaymentMethodsRequest,
  PaymentMethod,
  PaymentPlanQueries,
  PersonPaymentMethodsResponse,
  getClientSecretLink,
} from '@frontend/api-payment-plans';

export type UseCardOnFileJustifiApiProps = {
  isPublic: boolean;
};

export type CardOnFileJustifiApi = {
  useGetPaymentMethodSetupLink: (
    request: GetLocationPaymentMethodsRequest,
    options?: UseQueryOptions<PersonPaymentMethodsResponse, unknown, string | undefined>
  ) => UseQueryResult<string, unknown>;
  savePaymentMethodBySetupLink: typeof getClientSecretLink;
  useGetPaymentMethodsByPerson: (
    request: GetPersonPaymentMethodsRequest,
    options?: UseQueryOptions<PaymentMethod[], unknown, PaymentMethod[]>
  ) => UseQueryResult<PaymentMethod[], unknown>;
};

export const useCardOnFileJustifiApi = ({ isPublic }: UseCardOnFileJustifiApiProps) => {
  // Define the public implementation

  const getPublicImplementation = (): CardOnFileJustifiApi => {
    return {
      useGetPaymentMethodSetupLink: () => {
        console.warn('unimplemented method');
        return {
          data: undefined,
          isLoading: false,
          isError: false,
          error: null,
        } as UseQueryResult<string, unknown>;
      },

      savePaymentMethodBySetupLink: (
        _setupLink: string | undefined,
        _data: {
          data: {
            personId?: string;
            locationId: string;
            paymentMethodId?: string;
          };
        }
      ) => {
        console.warn('unimplemented method');
        return Promise.resolve('');
      },
      useGetPaymentMethodsByPerson: () => {
        console.warn('unimplemented method');
        return {
          data: undefined,
          isLoading: false,
          isError: false,
          error: null,
        } as UseQueryResult<PaymentMethod[], unknown>;
      },
    };
  };

  // Define the authenticated implementation

  const getAuthenticatedImplementation = (): CardOnFileJustifiApi => {
    return {
      /**
       * Justifi: Save Payment method, step 1
       * Get a setup link (same endpoint used for stripe)
       *
       * TODO: Potentially need public endpoint equivalent
       */
      useGetPaymentMethodSetupLink: PaymentPlanQueries.useGetPaymentMethodSetupLink,

      /**
       * Justifi: Save Payment method, step 2
       * Use the setup link, obtained in step 1.  Pass in person ID and payment method ID.
       *
       * TODO: Potentially need public endpoint equivalent
       */
      savePaymentMethodBySetupLink: getClientSecretLink,

      useGetPaymentMethodsByPerson: PaymentPlanQueries.useGetPaymentMethodsByPerson,
    };
  };

  const apiImplementation = useMemo(() => {
    return isPublic ? getPublicImplementation() : getAuthenticatedImplementation();
  }, [isPublic]);

  // Return the appropriate implementation based on isPublic

  return apiImplementation;
};
